import { History, Save, Upload, Loader2, Lock, LockOpen } from "lucide-react";
import { Section } from "./Section";

export function BackupSettings({
  t, input, settings, open, onToggle,
  autoBk, setAutoBk, extBk, setExtBk, s3Secret, setS3Secret, ftpPass, setFtpPass,
  extTestBusy, testExternal, smtpConfigured,
  backupList, backupRunBusy, runBackupNow, restoreSaved, downloadSaved, deleteSaved,
  restoreRef, restoreBusy, doRestore, downloadBackup, updateRef, updateBusy, doUpdate,
}) {
  return (
    <Section icon={History} iconColor="text-amber-400" title={t("Kopia zapasowa i aktualizacja")}
      open={open} onToggle={onToggle} testid="settings-sec-backup">
      <div className="text-xs font-semibold text-slate-300 mb-1.5">{t("Kopia zapasowa danych")}</div>
      <p className="text-[11px] text-slate-400 mb-2">{t("Pobierz pełną kopię (baza danych + zdjęcia/podpisy) jako plik ZIP i przechowuj ją w bezpiecznym miejscu.")}</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={downloadBackup} data-testid="backup-download-btn"
          className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm">
          <Save size={14} /> {t("Pobierz kopię zapasową")}
        </button>
        <button type="button" disabled={restoreBusy} onClick={() => restoreRef.current?.click()} data-testid="backup-restore-btn"
          className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm disabled:opacity-60">
          {restoreBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("Przywróć z pliku")}
        </button>
        <input ref={restoreRef} type="file" accept=".zip,.enc,.zip.enc" className="hidden" onChange={doRestore} data-testid="backup-restore-input" />
      </div>
      <p className="text-[11px] text-amber-400/80 mt-2">{t("Przywracanie nadpisuje wszystkie obecne dane — zrób najpierw kopię zapasową.")}</p>

      <div className="mt-4 border-t border-slate-700 pt-3">
        <div className="text-xs font-semibold text-slate-300 mb-1.5">{t("Automatyczna kopia (codziennie)")}</div>
        <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="auto-backup-label">
          <input type="checkbox" checked={autoBk.auto_backup_enabled}
            onChange={(e) => setAutoBk({ ...autoBk, auto_backup_enabled: e.target.checked })}
            data-testid="auto-backup-toggle" className="w-4 h-4 accent-amber-500" />
          <span className="text-sm text-slate-200">{t("Włącz codzienną automatyczną kopię (2:00)")}</span>
        </label>
        <label className="text-[11px] text-slate-400 block mb-1">{t("Ile ostatnich kopii przechowywać")}</label>
        <input type="number" min="1" max="90" value={autoBk.auto_backup_keep}
          onChange={(e) => setAutoBk({ ...autoBk, auto_backup_keep: e.target.value })}
          data-testid="auto-backup-keep" className={`${input} mb-2`} />
        <label className="text-[11px] text-slate-400 block mb-1">{t("E-mail do wysyłki kopii (opcjonalnie)")}</label>
        <input value={autoBk.auto_backup_email} onChange={(e) => setAutoBk({ ...autoBk, auto_backup_email: e.target.value })}
          placeholder={smtpConfigured ? "np. it@firma.pl" : t("skonfiguruj najpierw SMTP w sekcji Powiadomienia")}
          disabled={!smtpConfigured} data-testid="auto-backup-email" className={input} />
        <p className="text-[11px] text-slate-500 mt-1">
          {smtpConfigured
            ? t("Kopia zostanie wysłana e-mailem (jeśli plik < 15 MB). Puste = użyj adresu z sekcji Powiadomienia.")
            : t("Wysyłka e-mail wymaga skonfigurowanego SMTP (sekcja Powiadomienia). Kopie i tak zapisują się na serwerze.")}
        </p>
        <p className="text-[11px] text-amber-400/80 mt-1">{t("Pamiętaj zapisać ustawienia (przycisk na dole), aby włączyć automat.")}</p>
        <label className="flex items-center gap-2 cursor-pointer mt-3 pt-3 border-t border-slate-700" data-testid="require-enc-label">
          <input type="checkbox" checked={autoBk.require_encrypted_backup}
            onChange={(e) => setAutoBk({ ...autoBk, require_encrypted_backup: e.target.checked })}
            data-testid="require-enc-toggle" className="w-4 h-4 accent-cyan-500" />
          <span className="text-sm text-slate-200">{t("Nie twórz kopii bez aktywnego klucza (wymuś szyfrowanie)")}</span>
        </label>
        <p className="text-[11px] text-slate-500 mt-1">{t("Gdy włączone, a klucz szyfrujący nie jest aktywny — kopia (ręczna i automatyczna) zostanie zablokowana, aby nie powstał jawny backup.")}</p>
      </div>

      <div className="mt-4 border-t border-slate-700 pt-3" data-testid="ext-backup-block">
        <div className="text-xs font-semibold text-slate-300 mb-1.5">{t("Zewnętrzne cele kopii (poza serwerem)")}</div>
        <p className="text-[11px] text-slate-400 mb-2">{t("Wysyłaj każdą kopię również na zewnętrzny magazyn — dane przetrwają nawet całkowitą awarię serwera. Konfiguracja opcjonalna. Po zapisaniu użyj „Testuj połączenie”.")}</p>

        {/* S3 */}
        <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="s3-enable-label">
          <input type="checkbox" checked={extBk.backup_s3_enabled}
            onChange={(e) => setExtBk({ ...extBk, backup_s3_enabled: e.target.checked })}
            data-testid="s3-enable-toggle" className="w-4 h-4 accent-amber-500" />
          <span className="text-sm text-slate-200">{t("Wysyłaj do magazynu S3 (AWS, MinIO, Wasabi, Backblaze B2)")}</span>
        </label>
        {extBk.backup_s3_enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 pl-6">
            <input value={extBk.backup_s3_endpoint} onChange={(e) => setExtBk({ ...extBk, backup_s3_endpoint: e.target.value })}
              placeholder={t("Endpoint (puste = AWS S3)")} data-testid="s3-endpoint" className={input} autoComplete="off" />
            <input value={extBk.backup_s3_region} onChange={(e) => setExtBk({ ...extBk, backup_s3_region: e.target.value })}
              placeholder={t("Region (np. eu-central-1)")} data-testid="s3-region" className={input} autoComplete="off" />
            <input value={extBk.backup_s3_bucket} onChange={(e) => setExtBk({ ...extBk, backup_s3_bucket: e.target.value })}
              placeholder={t("Nazwa bucketu")} data-testid="s3-bucket" className={input} autoComplete="off" />
            <input value={extBk.backup_s3_prefix} onChange={(e) => setExtBk({ ...extBk, backup_s3_prefix: e.target.value })}
              placeholder={t("Prefiks / folder (opcjonalnie)")} data-testid="s3-prefix" className={input} autoComplete="off" />
            <input value={extBk.backup_s3_access_key} onChange={(e) => setExtBk({ ...extBk, backup_s3_access_key: e.target.value })}
              placeholder={t("Access Key ID")} data-testid="s3-access-key" className={input} autoComplete="off" />
            <input type="password" value={s3Secret} onChange={(e) => setS3Secret(e.target.value)}
              placeholder={settings.backup_s3_secret_set ? t("Secret (ustawiony — puste = bez zmian)") : t("Secret Access Key")}
              data-testid="s3-secret-key" className={input} autoComplete="off" />
          </div>
        )}

        {/* FTP */}
        <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="ftp-enable-label">
          <input type="checkbox" checked={extBk.backup_ftp_enabled}
            onChange={(e) => setExtBk({ ...extBk, backup_ftp_enabled: e.target.checked })}
            data-testid="ftp-enable-toggle" className="w-4 h-4 accent-amber-500" />
          <span className="text-sm text-slate-200">{t("Wysyłaj na serwer FTP / FTPS")}</span>
        </label>
        {extBk.backup_ftp_enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 pl-6">
            <input value={extBk.backup_ftp_host} onChange={(e) => setExtBk({ ...extBk, backup_ftp_host: e.target.value })}
              placeholder={t("Host FTP")} data-testid="ftp-host" className={input} autoComplete="off" />
            <input value={extBk.backup_ftp_port} onChange={(e) => setExtBk({ ...extBk, backup_ftp_port: e.target.value })}
              placeholder={t("Port (21)")} data-testid="ftp-port" className={input} autoComplete="off" />
            <input value={extBk.backup_ftp_user} onChange={(e) => setExtBk({ ...extBk, backup_ftp_user: e.target.value })}
              placeholder={t("Użytkownik")} data-testid="ftp-user" className={input} autoComplete="off" />
            <input type="password" value={ftpPass} onChange={(e) => setFtpPass(e.target.value)}
              placeholder={settings.backup_ftp_pass_set ? t("Hasło (ustawione — puste = bez zmian)") : t("Hasło")}
              data-testid="ftp-pass" className={input} autoComplete="off" />
            <input value={extBk.backup_ftp_dir} onChange={(e) => setExtBk({ ...extBk, backup_ftp_dir: e.target.value })}
              placeholder={t("Katalog docelowy (opcjonalnie)")} data-testid="ftp-dir" className={input} autoComplete="off" />
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
              <input type="checkbox" checked={extBk.backup_ftp_tls}
                onChange={(e) => setExtBk({ ...extBk, backup_ftp_tls: e.target.checked })}
                data-testid="ftp-tls-toggle" className="w-4 h-4 accent-amber-500" />
              {t("Szyfrowanie FTPS (TLS)")}
            </label>
          </div>
        )}

        <button type="button" disabled={extTestBusy} onClick={testExternal} data-testid="ext-backup-test-btn"
          className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm disabled:opacity-60">
          {extTestBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("Testuj połączenie")}
        </button>
        <p className="text-[11px] text-slate-500 mt-1">{t("Test używa zapisanych ustawień — najpierw kliknij „Zapisz ustawienia”. Jeśli wysyłka zawiedzie, wyślemy alert e-mail na adres z sekcji kopii.")}</p>
      </div>

      <div className="mt-4 border-t border-slate-700 pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-300">{t("Historia kopii na serwerze")}</div>
          <button type="button" disabled={backupRunBusy} onClick={runBackupNow} data-testid="backup-run-now-btn"
            className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 rounded px-3 py-1.5 text-xs disabled:opacity-60">
            {backupRunBusy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {t("Utwórz kopię teraz")}
          </button>
        </div>
        {backupList.length === 0 ? (
          <p className="text-[11px] text-slate-500" data-testid="backup-list-empty">{t("Brak zapisanych kopii na serwerze.")}</p>
        ) : (
          <div className="space-y-1.5" data-testid="backup-list">
            {backupList.map((b) => {
              const encrypted = b.encrypted || String(b.name || "").endsWith(".zip.enc");
              return (
              <div key={b.name} className="flex items-center justify-between bg-slate-900/60 border border-slate-700 rounded px-2.5 py-1.5" data-testid={`backup-row-${b.name}`}>
                <div className="min-w-0 flex items-center gap-2">
                  {encrypted
                    ? <Lock size={14} className="text-emerald-400 shrink-0" data-testid={`backup-enc-${b.name}`} title={t("Zaszyfrowana")} />
                    : <LockOpen size={14} className="text-amber-400 shrink-0" data-testid={`backup-plain-${b.name}`} title={t("Niezaszyfrowana")} />}
                  <div className="min-w-0">
                    <div className="text-[11px] text-slate-200 truncate">{b.created?.slice(0, 16).replace("T", " ")}
                      <span className={`ml-1.5 text-[9px] px-1 py-0.5 rounded ${encrypted ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{encrypted ? t("zaszyfrowana") : t("jawna")}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">{b.name} · {(b.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <button type="button" onClick={() => restoreSaved(b.name)} data-testid={`backup-restore-${b.name}`} className="text-[11px] text-cyan-400 hover:underline">{t("Przywróć")}</button>
                  <button type="button" onClick={() => downloadSaved(b.name)} data-testid={`backup-dl-${b.name}`} className="text-[11px] text-slate-300 hover:underline">{t("Pobierz")}</button>
                  <button type="button" onClick={() => deleteSaved(b.name)} data-testid={`backup-del-${b.name}`} className="text-[11px] text-red-400 hover:underline">{t("Usuń")}</button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-slate-700 pt-3">
        <div className="text-xs font-semibold text-slate-300 mb-1.5">{t("Aktualizacja programu (z pliku)")}</div>
        <p className="text-[11px] text-slate-400 mb-2">{t("Wgraj paczkę aktualizacji (fiberpass-full.zip). Program zainstaluje ją i zrestartuje się sam — bez logowania na serwer.")}</p>
        <button type="button" disabled={updateBusy} onClick={() => updateRef.current?.click()} data-testid="app-update-btn"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded px-3 py-2 text-sm disabled:opacity-60">
          {updateBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("Wgraj i zaktualizuj")}
        </button>
        <input ref={updateRef} type="file" accept=".zip" className="hidden" onChange={doUpdate} data-testid="app-update-input" />
        <p className="text-[11px] text-slate-500 mt-2">{t("Po zakończeniu (2–5 min) odśwież stronę Ctrl+F5. Nie zamykaj karty w trakcie aktualizacji.")}</p>
      </div>
    </Section>
  );
}
